import { useTranslation } from "react-i18next";
import { cn } from "@nexus/ui";
import { Check } from "lucide-react";
import type { ImportReadiness, ImportWorkflowStep } from "./import-readiness";

const STEP_ORDER: ImportWorkflowStep[] = ["import", "vendors", "dates", "issues", "finish"];

interface ImportWorkflowStepsProps {
  readiness: ImportReadiness;
}

export function ImportWorkflowSteps({ readiness }: ImportWorkflowStepsProps) {
  const { t } = useTranslation();
  const activeIndex = STEP_ORDER.indexOf(readiness.activeStep);

  return (
    <nav
      aria-label={t("marketplace.admin.import.steps.navLabel")}
      className="rounded-xl border border-border/60 bg-card p-4"
    >
      <ol className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {STEP_ORDER.map((step, index) => {
          const isComplete = readiness.steps[step];
          const isActive = step === readiness.activeStep;
          const isPast = index < activeIndex;

          return (
            <li key={step} className="flex flex-1 items-center gap-2 sm:flex-col sm:gap-1.5">
              <div className="flex items-center gap-2 sm:flex-col">
                <span
                  className={cn(
                    "flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold tabular-nums",
                    isComplete
                      ? "border-primary bg-primary text-primary-foreground"
                      : isActive
                        ? "border-primary text-primary"
                        : "border-border text-muted-foreground",
                  )}
                >
                  {isComplete && !isActive ? (
                    <Check className="h-3.5 w-3.5" aria-hidden />
                  ) : (
                    index + 1
                  )}
                </span>
                <span
                  className={cn(
                    "text-xs font-medium sm:text-center",
                    isActive ? "text-foreground" : "text-muted-foreground",
                  )}
                >
                  {t(`marketplace.admin.import.steps.${step}`)}
                </span>
              </div>
              {index < STEP_ORDER.length - 1 ? (
                <div
                  className={cn(
                    "hidden h-px flex-1 sm:block",
                    isPast || isComplete ? "bg-primary/40" : "bg-border/60",
                  )}
                  aria-hidden
                />
              ) : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
